import { removeExternalEmail } from "../api/email";
import { unlockAccount, addToMfaPilot, startOffNetworkEnrollment } from "../api/users";
import { useStatusMessage } from "../contexts/StatusMessageContext";
import { useApiActivity } from "../contexts/ApiActivityContext";
import type { UserInfo } from "../types/user";

interface UseAccountActionsReturn {
  resetSecondaryEmail: () => Promise<boolean>;
  unlockAccount: () => Promise<boolean>;
  addToMfaPilot: () => Promise<boolean>;
  enableOffNetworkEnrollment: () => Promise<boolean>;
}

const useAccountActions = (
  userInfo: UserInfo,
  searchUser: () => Promise<void>
): UseAccountActionsReturn => {
  const { setStatus, clear: clearStatus } = useStatusMessage();
  const { beginActivity, endActivity } = useApiActivity();

  const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
  const getDisplayName = () =>
    `${(userInfo as any).firstName ?? ""} ${(userInfo as any).lastName ?? ""}`.trim();
  const getOktaId = () => (userInfo as any).okta?.id;

  const resetSecondaryEmail = async (): Promise<boolean> => {
    clearStatus();
    setStatus({
      title: "Removing Secondary Email",
      message: `Removing secondary email for ${getDisplayName()}...`,
      isError: false,
      isOkButtonVisible: false,
    });
    beginActivity("account");

    try {
      const response = await removeExternalEmail(getOktaId());
      if (response.errorMsg) {
        setStatus({
          title: "Error Removing Secondary Email",
          message: response.errorMsg,
          isError: true,
          isOkButtonVisible: true,
        });
        return false;
      }

      setStatus({
        title: "Secondary Email Removed",
        message: "Secondary email was removed successfully.",
        isError: false,
        isOkButtonVisible: true,
      });

      await sleep(2000);
      await searchUser();
      return true;
    } catch (error) {
      setStatus({
        title: "Error Removing Secondary Email",
        message: `Failed to remove secondary email: ${error}`,
        isError: true,
        isOkButtonVisible: true,
      });
      return false;
    } finally {
      endActivity("account");
    }
  };

  const unlockAccountAction = async (): Promise<boolean> => {
    clearStatus();
    setStatus({
      title: "Unlocking Account",
      message: `Unlocking account for ${getDisplayName()}...`,
      isError: false,
      isOkButtonVisible: false,
    });
    beginActivity("account");

    try {
      const response = await unlockAccount(getOktaId());

      if (response.errorMsg) {
        setStatus({
          title: "Error Unlocking Account",
          message: response.errorMsg,
          isError: true,
          isOkButtonVisible: true,
        });
        return false;
      } else {
        const message =
          "Account unlocked. Please allow up to 15 minutes for Active Directory to sync globally.";
        setStatus({
          title: "Account Unlocked",
          message,
          isError: false,
          isOkButtonVisible: true,
        });
        await searchUser();
        return true;
      }
    } catch (error) {
      setStatus({
        title: "Error Unlocking Account",
        message: `Failed to unlock account: ${error}`,
        isError: true,
        isOkButtonVisible: true,
      });
      return false;
    } finally {
      endActivity("account");
    }

  };

  const addToMfaPilotAction = async (): Promise<boolean> => {
    clearStatus();
    setStatus({
      title: "Adding to MFA-Reset Pilot",
      message: `Adding ${getDisplayName()} to the MFA-Reset Pilot group...`,
      isError: false,
      isOkButtonVisible: false,
    });
    beginActivity("account");

    try {
      const result = await addToMfaPilot(getOktaId());

      if (result.errorMsg) {
        setStatus({
          title: "Error Adding to MFA-Reset Pilot",
          message: result.errorMsg,
          isError: true,
          isOkButtonVisible: true,
        });
        return false;
      } else {
        setStatus({
          title: "User Added to MFA-Reset Pilot",
          message: `User ${getDisplayName()} has been added to the MFA-Reset Pilot group. They can now use https://mfa-reset.cis-iam-okta-prod.nikecloud.com to reset their MFA factors.`,
          isError: false,
          isOkButtonVisible: true,
        });
        return true;
      }
    } catch {
      setStatus({
        title: "Error Adding to MFA-Reset Pilot",
        message: "Failed to add user to MFA-Reset Pilot group",
        isError: true,
        isOkButtonVisible: true,
      });
      return false;
    } finally {
      endActivity("account");
    }
  };

  const enableOffNetworkEnrollment = async (): Promise<boolean> => {
    clearStatus();
    setStatus({
      title: "Enabling Off-Network MFA Enrollment",
      message: `Enabling off-network MFA enrollment for ${getDisplayName()}...`,
      isError: false,
      isOkButtonVisible: false,
    });
    beginActivity("account");

    try {
      const result = await startOffNetworkEnrollment(getOktaId());

      if (result.errorMsg) {
        setStatus({
          title: "Error Enabling Off-Network MFA Enrollment",
          message: result.errorMsg,
          isError: true,
          isOkButtonVisible: true,
        });
        return false;
      } else {
        setStatus({
          title: "Off-Network MFA Enrollment Enabled",
          message: "This user can now enroll MFA factors from off the Nike network for the next 24 hours.",
          isError: false,
          isOkButtonVisible: true,
        });
        return true;
      }
    } catch {
      setStatus({
        title: "Error Enabling Off-Network MFA Enrollment",
        message: "Failed to enable off-network MFA enrollment",
        isError: true,
        isOkButtonVisible: true,
      });
      return false;
    } finally {
      endActivity("account");
    }
  };

  return {
    resetSecondaryEmail,
    unlockAccount: unlockAccountAction,
    addToMfaPilot: addToMfaPilotAction,
    enableOffNetworkEnrollment,
  };
};

export default useAccountActions;
